import {Component, OnInit, OnDestroy, HostListener} from '@angular/core';
import {Sidenavaction} from '../../../../../../assets/js/application/sidenavaction';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import * as moment from 'moment';
import {CultureCategoryService} from '../../../../../services/cultureCategory.service';
import CultureCategoryModel from '../../../../../services/cultureCategory.model';
import {ToastrService} from 'ngx-toastr';
import {CommonMethodsService} from 'src/app/services/commonMethods.service';
import {LocationStrategy} from '@angular/common';
import {PreviousRouteService} from 'src/app/services/previousRouteService';
import {BatchLoader} from "../../../../../Utils/batch-loader.util";

declare var $: any;

/**
 * CultureCategoryComponent
 *
 * This component is responsible for managing Culture Categories within the Pond Management module.
 * It provides a user interface for administrators to view, create, update, and search for culture categories.
 * The component implements infinite scrolling for efficient data display and uses reactive forms for data entry.
 *
 * Key Responsibilities:
 * - Fetch and display a list of all culture categories.
 * - Provide search functionality to filter categories by name or abbreviation.
 * - Manage the creation of new culture categories with validation.
 * - Handle updates to existing culture categories.
 * - Implement infinite scrolling using BatchLoader to handle large datasets.
 * - Manage UI state for loading indicators, form visibility, and submission status.
 *
 * Features:
 * - **CRUD Operations**: Create and Update culture categories.
 * - **Real-time Search**: Filter list by category name or abbreviation.
 * - **Infinite Scroll**: Load data in batches to improve performance.
 * - **Form Validation**: Ensures category names follow specific patterns (alphanumeric, spaces).
 * - **Date Formatting**: Formats 'updatedAt' and 'createdAt' timestamps for display.
 *
 * Data Flow:
 * 1. Component initializes (`ngOnInit`).
 * 2. Fetches all culture categories from `CultureCategoryService`.
 * 3. Formats dates using `moment`.
 * 4. Initializes `BatchLoader` with the full dataset.
 * 5. User interactions (scroll, search, add/edit) trigger state updates and service calls.
 * 6. `addUpdateCultureCategory` sends data to backend and refreshes the list on success.
 *
 * @component
 * @selector app-culture-category
 * @example
 * <app-culture-category></app-culture-category>
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-culture-category',
  templateUrl: './culture-category.component.html',
  styleUrls: ['./culture-category.component.scss']
})
export class CultureCategoryComponent implements OnInit, OnDestroy {
  /**
   * Array holding all fetched culture categories from the backend.
   * Acts as the source of truth for the list before filtering.
   * @type {CultureCategoryModel[]}
   * @default []
   */
  public cultureCategories: CultureCategoryModel[] = [];

  /**
   * Array holding the currently displayed culture categories.
   * This list is populated by the BatchLoader (subset of cultureCategories or filteredCultureCategory).
   * @type {CultureCategoryModel[]}
   * @default []
   */
  public cultureCategoryList: CultureCategoryModel[] = [];

  /**
   * Model for the current culture category being added or edited.
   * Bound to the form for data manipulation.
   * @type {CultureCategoryModel}
   */
  public cultureCategory: CultureCategoryModel = new CultureCategoryModel();

  /**
   * Array holding culture categories that match the current search criteria.
   * Used as the source for BatchLoader when filtering is active.
   * @type {CultureCategoryModel[]}
   * @default []
   */
  public filteredCultureCategory: CultureCategoryModel[] = [];

  /**
   * Current search query string entered by the user.
   * @type {string}
   */
  public search: string;

  /**
   * Reactive form group for culture category details (name, abbreviation).
   * @type {FormGroup}
   */
  registerForm: FormGroup;

  /**
   * Flag indicating if the form has been submitted.
   * Used to trigger validation error display in the UI.
   * @type {boolean}
   * @default false
   */
  submitted = false;

  /**
   * Flag indicating if the form is in 'Create' mode.
   * @type {boolean}
   */
  public submitButton: boolean;

  /**
   * Flag indicating if the form is in 'Update' mode.
   * @type {boolean}
   */
  public updateButton: boolean;

  /**
   * Flag to prevent double clicks on submit actions.
   * @type {boolean}
   */
  public cultureCategoriesDoubleClick: boolean;

  /**
   * Flag indicating if no data is available for display.
   * @type {boolean}
   * @default false
   */
  public NoDataMessage: boolean = false;

  /**
   * Flag to trigger clearing of the search input field.
   * @type {boolean}
   * @default false
   */
  public clearSearchInput: boolean = false;

  /**
   * Flag to control the visibility of the main page loader.
   * @type {boolean}
   * @default false
   */
  public enableDisablePageLoader: boolean = false;

  /**
   * Flag to control the visibility of the list loader spinner during infinite scroll.
   * @type {boolean}
   * @default false
   */
  public listLoader = false;              // Show listLoader spinner

  /**
   * Tracks the number of items currently loaded by the batch loader.
   * @type {number}
   * @default 0
   */
  public startIndex = 0;             // Tracks how much has been loaded

  /**
   * Flag indicating if a search filter is currently active.
   * @type {boolean}
   * @default false
   */
  public isFiltering: boolean = false;

  /**
   * Flag indicating if the list is empty (no data or no search results).
   * @type {boolean}
   * @default false
   */
  public noList: boolean = false;

  /**
   * Timeout reference for search input debouncing.
   * @type {any}
   */
  searchTimeout: any;

  /**
   * Count of fields in the form, used for layout adjustments.
   * @type {number}
   * @default 0
   */
  public formFieldCount: number = 0;

  /**
   * Flag to toggle the visibility of the add/edit form.
   * @type {boolean}
   * @default false
   */
  public showForm: boolean = false;

  /**
   * Constructor for CultureCategoryComponent
   *
   * Initializes the component, injects required services, and sets up the reactive form.
   *
   * Form Validation Rules:
   * - name: Required, Min 3, Max 20, Pattern (alphanumeric, spaces, specific symbols).
   * - abbreviation: Required, Max 10, Pattern (alphanumeric).
   *
   * @constructor
   * @param {ToastrService} toasterService - Service for displaying toast notifications.
   * @service ToastrService
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @service Sidenavaction
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @service FormBuilder
   * @param {CultureCategoryService} cutureCategoryService - Service for Culture Category API operations.
   * @service CultureCategoryService
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods and error handling.
   * @service CommonMethodsService
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history.
   * @service PreviousRouteService
   */
  constructor(private toasterService: ToastrService, public sidenavaction: Sidenavaction, public formBuilder: FormBuilder, public cutureCategoryService: CultureCategoryService, public commonMethodsService: CommonMethodsService,
              private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.cultureCategory = new CultureCategoryModel();
    /*form validation*/
    this.registerForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(20), Validators.pattern('^[a-zA-Z ]*$')]],
      abbreviation: ['', [Validators.required, Validators.maxLength(10), Validators.pattern('^[a-zA-Z0-9]*$')]],
    });
    // Calculate field count after form is built // new
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  /**
   * Utility for handling batch loading of data for infinite scroll.
   * @type {BatchLoader<any>}
   * @private
   */
  private batchLoader: BatchLoader<any>;

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Performs the following initialization steps:
   * 1. Sets up side navigation.
   * 2. Resets component state and form.
   * 3. Tracks the current route.
   * 4. Fetches all culture categories from the backend.
   * 5. Formats the 'updatedAt' and 'createdAt' dates for each category.
   * 6. Initializes the batch loader and loads the first batch of data.
   *
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @lifecycle Angular component initialization hook
   */
  public async ngOnInit() {
    this.sidenavaction.x_navigation();
    window.scrollTo(0, 0);
    this.enableDisablePageLoader = true;
    this.clear();
    this.cultureCategories = [];
    this.cultureCategoryList = [];
    this.filteredCultureCategory = [];
    this.isFiltering = false;
    this.search = '';
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    try {
      this.cultureCategories = await this.cutureCategoryService.getAllCultureCategories();
      if (this.cultureCategories.length > 0) {
        this.cultureCategories.forEach(eachCultureCategory => {
          eachCultureCategory.updatedAt = JSON.parse(JSON.stringify(moment(eachCultureCategory.updatedAt).format('DD-MMM-YYYY hh:mm:ss A')));
          eachCultureCategory.createdAt = JSON.parse(JSON.stringify(moment(eachCultureCategory.createdAt).format('DD-MMM-YYYY hh:mm:ss A')));

        });
      }
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader?.reset();     // Clear state
      await this.batchLoader?.loadNextBatch(true); // Load first batch
      this.enableDisablePageLoader = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.enableDisablePageLoader = false;
    }
  }

  /**
   * Host listener for window scroll events.
   * Triggers the check for loading the next batch of data when scrolling near the bottom.
   *
   * @method onScroll
   * @returns {void}
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * Timeout reference for scroll event debouncing.
   * @type {any}
   * @private
   */
  private scrollTimeout: any;

  /**
   * Checks scroll position and loads next batch if near bottom.
   * Debounced to prevent excessive calls.
   *
   * @method checkScrollAndLoad
   * @private
   * @returns {void}
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * Initializes the BatchLoader instance.
   * Configures the source list (filtered or full) and the callback to update the displayed list.
   *
   * @method initializeBatchLoader
   * @private
   * @returns {void}
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredCultureCategory : this.cultureCategories,
      (updatedList) => {
        this.cultureCategoryList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * Angular lifecycle hook - Component destruction
   *
   * Cleans up resources:
   * - Cancels ongoing HTTP requests.
   * - Destroys and resets the batch loader.
   *
   * @method ngOnDestroy
   * @returns {void}
   * @lifecycle Angular component destruction hook
   */
  public ngOnDestroy(): void {
    this.cutureCategoryService.cancelOnGoingHttpRequests()
    this.batchLoader?.markAsDestroyed();
    this.batchLoader?.reset();
  }

  /**
   * Getter for easy access to form controls in the template.
   *
   * @method cultureCategoryFormValidation
   * @returns {AbstractControl} Form controls
   */
  public get cultureCategoryFormValidation() {
    return this.registerForm.controls;
  }

  /**
   * Filters the culture category list based on the search query.
   *
   * - Filters by name or abbreviation (case-insensitive).
   * - Updates `filteredCultureCategory`.
   * - Resets the batch loader to display filtered results.
   *
   * @async
   * @method applySearch
   * @returns {Promise<void>}
   * @side-effect Updates `filteredCultureCategory`, `cultureCategoryList`, `noList`, `isFiltering`.
   */
  public async applySearch(): Promise<void> {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.cultureCategories.filter(route =>
        route.name?.toLowerCase().includes(query) ||
        route.abbreviation?.toLowerCase().includes(query)
      )
      : this.cultureCategories;
    this.filteredCultureCategory = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      //  Force noList = true if nothing to show
      this.noList = true;
      this.cultureCategoryList = [];
      return;
    }
    // optional: handle scroll if user already at bottom
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  /**
   * Debounces search input changes.
   * Calls `applySearch` after a delay to prevent excessive filtering.
   *
   * @method onSearchChanged
   * @returns {void}
   */
  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch(); // debounced
    }, 300);
  }

  /**
   * Resets the form and component state to default values.
   *
   * @method clear
   * @returns {void}
   */
  public clear() {
    this.registerForm.reset();
    this.showForm = false;
    this.cultureCategory = new CultureCategoryModel();
    this.cultureCategoriesDoubleClick = false;
    this.submitButton = false;
    this.updateButton = false;
    this.submitted = false;
  }

  /**
   * Handles the submission of the Culture Category form (Add or Update).
   *
   * - Validates the form.
   * - Calls `create` or `updateOneByID` service methods based on mode.
   * - Displays success notification.
   * - Refreshes the list via `ngOnInit`.
   *
   * @async
   * @method addUpdateCultureCategory
   * @returns {Promise<void>}
   * @throws {Error} If the service call fails.
   */
  public async addUpdateCultureCategory() {
    this.submitted = true;
    // stop here if form is invalid
    if (this.registerForm.invalid) {
      return;
    }
    try {
      // GET FORM VALUES
      const formData = this.registerForm.value; // contains updated values
      this.cultureCategoriesDoubleClick = true;
      if (this.submitButton) {
        await this.cutureCategoryService.create(formData);
        this.toasterService.success('Culture category created successfully', '', {
          timeOut: 2000
        });
      } else {
        const updateCulture = {...formData}
        delete updateCulture.id;
        await this.cutureCategoryService.updateOneByID(this.cultureCategory.id, updateCulture);
        this.toasterService.success('Culture category updated successfully', '', {
          timeOut: 2000
        });
      }
      await this.ngOnInit();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.cultureCategoriesDoubleClick = false;
    }
  }

  /**
   * Retrieves a Culture Category by ID and populates the form for editing.
   * Sets the component to 'Update' mode.
   *
   * @method getCultureCategoryById
   * @param {string} id - The ID of the culture category to edit.
   * @returns {void}
   */
  public getCultureCategoryById(id: string) {
    this.showForm = true;
    this.submitButton = false;
    this.cultureCategoriesDoubleClick = false;
    this.updateButton = true;
    const foundCultureCategory = this.cultureCategoryList.find(uom => uom?.id === id);
    this.cultureCategory = foundCultureCategory ? {...foundCultureCategory} : new CultureCategoryModel(); // clone object
    if (this.cultureCategory) {
      this.registerForm.patchValue({
        name: this.cultureCategory.name,
        abbreviation: this.cultureCategory.abbreviation,
      });
    }
  }

  /**
   * Opens the form in 'Create' mode.
   *
   * @method openForm
   * @returns {void}
   */
  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    // Count form fields to toggle layout
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }
}
